import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '@/i18n/use-translations';
import Modal from '@/components/common/modal';
import { useEventStore } from '@/state/stores/use-event-store';
import { Box, Button, Divider, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import EventCatalogProduct from '@/components/events/event-catalog/event-catalog-product.tsx';
import { PRODUCT_KEYS } from '@/types/dtos/orders/response/product.dto.ts';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { resolveQuotaScopeForEvent } from '@/helpers/product-quota.helper';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import {
  buildInviteModalPreview,
  type InvitePayloadRow,
} from '@/components/events/detail/tabs/contributors/invite-modal-preview.helpers';

export type InviteContributorModalSkip = {
  displayName: string;
  reasons: string[];
};

const InviteContributorModal = ({
  open,
  onModalClose,
  onConfirm,
  title,
  text,
  contributorsToInviteCount,
  skippedContributors,
  confirmDisabled,
  invitePayload,
  contributors,
  flowContext,
}: {
  open: boolean;
  onModalClose: () => void;
  onConfirm: () => void;
  title: string;
  text: string;
  contributorsToInviteCount: number;
  skippedContributors: InviteContributorModalSkip[];
  /** Extra disable (e.g. empty payload). Credits check still applies. */
  confirmDisabled?: boolean;
  /** Final POST payload (after skip filter); contributor order for the preview. */
  invitePayload?: InvitePayloadRow[];
  contributors?: ContributorDto[];
  /** Flow tab / slot-assets: scopes visibility of slot staging (same as invite build). */
  flowContext?: FlowDto;
}) => {
  const t = useTranslations();
  const { t: tI18n } = useTranslation();
  const event = useEventStore((s) => s.event);
  const eventForSlots = useEventStore((s) => s.eventOrganize);
  const eventFlows = useEventStore((s) => s.eventFlowsOrganize);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventFlowsPlan = useEventStore((s) => s.eventFlowsPlan);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventFlowsOrganize = useEventStore((s) => s.eventFlowsOrganize);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);
  const { scopeType: quotaScopeType, scopeId: quotaScopeId } = resolveQuotaScopeForEvent(event);
  const { data: quotaBalances } = useQuotaBalances(quotaScopeType, quotaScopeId);

  const invitePublicationCtx = useMemo(
    () =>
      eventPlan || eventFlowsPlan?.length || eventSessionsPlan?.length
        ? {
            eventPlan,
            eventFlowsPlan,
            eventSessionsPlan,
            eventFlowsOrganize,
            eventSessionsOrganize,
          }
        : undefined,
    [eventPlan, eventFlowsPlan, eventSessionsPlan, eventFlowsOrganize, eventSessionsOrganize],
  );

  const invitePreview = useMemo(() => {
    if (!invitePayload?.length || !contributors?.length) return [];
    const contributorIds = invitePayload.map((row) => row.contributorId);
    return buildInviteModalPreview(
      contributors,
      flowContext,
      contributorIds,
      eventForSlots,
      eventFlows,
      tI18n,
      invitePublicationCtx,
    );
  }, [invitePayload, contributors, flowContext, eventForSlots, eventFlows, tI18n, invitePublicationCtx]);

  const isEnoughContributorFeaturesAvailable = useMemo(() => {
    if (contributorsToInviteCount === 0) return true;
    const row = quotaBalances?.find((balance) => balance.quotaKey === 'contributors');
    const available = row?.available ?? 0;
    return available >= contributorsToInviteCount;
  }, [contributorsToInviteCount, quotaBalances]);

  const inviteDisabled = !!confirmDisabled || !isEnoughContributorFeaturesAvailable;

  const flowTitleSubtitle = flowContext?.title?.trim();
  const headerSubtitle =
    flowTitleSubtitle !== undefined && flowTitleSubtitle.length > 0 ? (
      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary" data-testid="invite-modal-flow-subtitle">
          {flowTitleSubtitle}
        </Typography>
        <Typography variant="body2" component="div">
          {text}
        </Typography>
      </Stack>
    ) : undefined;

  return (
    <Modal
      open={open}
      handleClose={onModalClose}
      size={invitePreview.length > 0 ? 'lg' : 'md'}
      data-testid="invite-contributor-modal"
      title={title}
      description={headerSubtitle ? undefined : text}
      subtitle={headerSubtitle}
      footer={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" color="info" onClick={onModalClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" color="info" disabled={inviteDisabled} onClick={onConfirm}>
            {t('common.invite')}
          </Button>
        </Box>
      }
    >
      {invitePreview.length > 0 && (
        <Box sx={{ mb: 2 }} data-testid="invite-modal-preview">
          <Typography variant="subtitle2" gutterBottom>
            {t('contributor.inviteContributorModal.includedTitle')}
          </Typography>
          <Box
            sx={{
              maxHeight: 320,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              px: 1.5,
              py: 1,
            }}
          >
            <Stack spacing={2} divider={<Divider flexItem />}>
              {invitePreview.map((row) => (
                <Stack key={row.contributorId} spacing={1}>
                  <Typography variant="body2" fontWeight={600}>
                    {row.displayName}
                  </Typography>
                  <Stack spacing={1.25} sx={{ pl: 0.5 }}>
                    {row.roles.map((rl, idx) => (
                      <Stack key={`${row.contributorId}-${idx}`} spacing={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          {rl.roleHeadline}
                        </Typography>
                        {rl.slotLines.length > 0 && (
                          <Stack spacing={0.25} sx={{ pl: 1 }}>
                            {rl.slotLines.map((line, li) => (
                              <Typography
                                key={`${row.contributorId}-${idx}-slot-${li}`}
                                variant="caption"
                                color="text.secondary"
                              >
                                {line}
                              </Typography>
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      )}
      {contributorsToInviteCount > 0 && !isEnoughContributorFeaturesAvailable && (
        <>
          <Box display="flex" alignItems="center" mb={2}>
            <InfoOutlined sx={{ mr: 1 }} />
            <Typography variant="body1">
              {t('contributor.inviteContributorModal.contributorsFeatureDescription')}
            </Typography>
          </Box>
          <EventCatalogProduct productKey={PRODUCT_KEYS.EVENT_CONTRIBUTOR} />
          <Divider sx={{ mt: 2 }} />
        </>
      )}
      {skippedContributors.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('contributor.inviteContributorModal.skippedTitle')}
          </Typography>
          <List
            dense
            sx={{
              maxHeight: 200,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {skippedContributors.map((row) => (
              <ListItem key={row.displayName} disablePadding sx={{ px: 1, py: 0.5, display: 'block' }}>
                <ListItemText
                  primary={row.displayName}
                  secondary={row.reasons
                    .map((r) =>
                      t(`contributor.inviteContributorModal.skipReason.${r}`, {
                        defaultValue: r,
                      }),
                    )
                    .join(' · ')}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Modal>
  );
};

export default InviteContributorModal;
